<template>
  <div class="actions-btn">
    <Button
      class="icon icon-left secondary sm no-dark-mode results_v2_back"
      label="Retourner au tableau de bord"
      icon="bi-arrow-left"
      @click="$router.push({ name: 'game-home' })"
    />
  </div>
  <div v-if="results">
    <div class="result2-main-container">
      <div class="result2-got">
        <img
          class="result2-got-logo"
          alt=""
          src="/images/resultsV2/ResultsGot.svg"
        />
        <div class="result2-got-section">
          <div class="result2-got-title">Vos résultats sont disponibles !</div>
          <span class="result2-got-text"
            >Félicitations, vous avez terminé votre aventure ! Vos résultats
            sont désormais disponibles. Veuillez noter que ce rapport propose
            une restitution enrichie et peut inclure des compétences distinctes
            de celles prises en compte par l’organisation à l’origine de votre
            test.
          </span>
        </div>
      </div>
      <div class="result2-results-area">
        <div class="result-v2-area-title">
          <div class="result-v2-bullet">
            <span class="result-v2-bullet-number">1</span>
            <div class="result-v2-bullet-point" />
          </div>
          <span class="result-v2-bullet-title">Vos points forts</span>
        </div>
        <div class="result2-top-skills">
          <span class="result2-subtitle"
            >Vos 3 compétences les plus développées</span
          >
          <span class="result2-subnote">
            Note : ce podium illustre un classement basé sur les
            caractéristiques propres à chaque profil, sans jugement de valeur.
          </span>
          <div class="results-module-container">
            <TopFeedback position="1" :skill="skill1" :top10="skill1Top10" />
            <TopFeedback position="2" :skill="skill2" :top10="skill2Top10" />
            <TopFeedback position="3" :skill="skill3" :top10="skill3Top10" />
          </div>
        </div>
      </div>
      <div class="result2-results-area" v-if="hasTendances">
        <div class="result-v2-area-title">
          <div class="result-v2-bullet">
            <span class="result-v2-bullet-number">2</span>
            <div class="result-v2-bullet-point" />
          </div>
          <span class="result-v2-bullet-title">Vos tendances</span>
        </div>
        <LeadershipStyle
          v-if="results.leadership_style"
          :style="JSON.parse(results.leadership_style)"
        />
        <!--<CommunicationStyle
          v-if="results.communication_style"
          :style="results.communication_style"
        />-->
        <LearnStyle
          v-if="results.learn_strategy_style"
          :style="results.learn_strategy_style"
        />
        <TeamStyle
          v-if="results.team_work_style"
          :style="results.team_work_style"
          :domination="results.domination_style"
        />
        <MotivationStyle
          v-if="results.motivation_style"
          :style="results.motivation_style"
        />
      </div>
      <div class="result2-results-area">
        <div class="result-v2-area-title">
          <div class="result-v2-bullet">
            <span class="result-v2-bullet-number">{{ hasTendances ? 3 : 2 }}</span>
            <div class="result-v2-bullet-point" />
          </div>
          <span class="result-v2-bullet-title">Votre zone d’amélioration</span>
        </div>
        <BottomFeedback :skill="worseSkill" />
      </div>
      <div class="result2-results-area">
        <div class="result-v2-area-title">
          <div class="result-v2-bullet">
            <span class="result-v2-bullet-number">4</span>
            <div class="result-v2-bullet-point" />
          </div>
          <span class="result-v2-bullet-title"
            >Vos badges de personnalité obtenus</span
          >
        </div>
        <div class="result2-badge-container">
          <BadgeItem v-for="badge in badgeList" :badge="badge" />
        </div>
      </div>
      <Button
        class="icon icon-left primary result2-gotop-button"
        label="Remonter en haut de la page"
        icon="bi-arrow-up"
        @click="scrollToTop()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useAssessmentStore } from "@/stores/assessment.js";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import TopFeedback from "@/views/game/resultsV2/top_feedback.vue";
import LeadershipStyle from "@/views/game/resultsV2/leadership_style.vue";
// import CommunicationStyle from "@/views/game/resultsV2/communication_style.vue";
import TeamStyle from "@/views/game/resultsV2/team_work_style.vue";
import LearnStyle from "@/views/game/resultsV2/learn_strategy_style.vue";
import MotivationStyle from "@/views/game/resultsV2/motivation_style.vue";
import BottomFeedback from "@/views/game/resultsV2/bottom_feedback.vue";
import BadgeItem from "@/views/game/resultsV2/badge_item.vue";

const isV2_1 = computed(() => {
  const date2_1 = new Date("2025-04-15T08:30Z"); // TODO env var ?
  let dateCandidateString = assessments.value[0]?.campaign.creation_time;
  // remove everything after the first "+"
  dateCandidateString = dateCandidateString.split("Z")[0];
  const dateCandidate = new Date(dateCandidateString);
  return dateCandidate > date2_1;
});

const isV2_1_2 = computed(() => {
  return results.value?.pb_solving_score >= 1000;
});

const assessmentStore = useAssessmentStore();
const { assessments, results } = storeToRefs(assessmentStore);

const router = useRouter();

if (assessments.value.length === 0) {
  await assessmentStore.getCandidateAssessments();
}

if (!results.value) router.push({ name: "game-home" });

// For each skill which is null, delete the entry. Useful for shorter campaigns that don't track certain skills.
Object.keys(results.value).forEach((key) => {
  if (results.value[key] === null) {
    delete results.value[key];
  }
});


//remove social_desire_score as it is not shown to talent
delete results.value.social_desire_score;

// Remove planning_score as it is not ready to be used yet until 2.1
if (!isV2_1.value) delete results.value.planning_score;
if (isV2_1_2.value) results.value.pb_solving_score -= 1000; // Adjust pb_solving_score for v2.1.2

// Order the skills by rank (A, B, C, D, E) first, then by score
const rankOrder = { A: 5, B: 4, C: 3, D: 2, E: 1 };
const orderSkills = Object.entries(results.value)
  .filter(([key, value]) => key.endsWith("_score") && value >= 0) // Only keys with '_score' & not negative
  .map(([key, value]) => {
    // Extract the skill name without the "_score" suffix
    const skillName = key.replace("_score", "");
    
    // Get the rank correctly from the results object
    let rank;
    if (typeof value === 'object' && value.rank) {
      // If results have structured format with rank property
      rank = value.rank;
    } else {
      // For simple numeric scores, calculate rank based on thresholds
      // Default rank calculation based on standard thresholds
      if (value >= 80) rank = 'A';
      else if (value >= 60) rank = 'B';
      else if (value >= 40) rank = 'C';
      else if (value >= 20) rank = 'D';
      else rank = 'E';
      
      // Special case for problem solving which has different thresholds
      if (key === 'pb_solving_score' && !isV2_1.value) {
        if (value >= 100) rank = 'A';
        else if (value >= 88.88) rank = 'B';
        else if (value >= 77.77) rank = 'C';
        else if (value >= 66.66) rank = 'D';
        else rank = 'E';
      }
    }
    
    return [skillName, value, rank];
  })
  .sort((a, b) => {
    // Sort primarily by rank (A > B > C > D > E)
    const rankDiff = rankOrder[b[2]] - rankOrder[a[2]];
    if (rankDiff !== 0) return rankDiff;
    
    // If ranks are the same, sort by score (higher score first)
    const scoreA = typeof a[1] === 'object' ? a[1].score || a[1].note : a[1];
    const scoreB = typeof b[1] === 'object' ? b[1].score || b[1].note : b[1];
    return scoreB - scoreA;
  });

let skill1 = orderSkills[0][0];
let skill2 = orderSkills[1][0];
let skill3 = orderSkills[2][0];

// Get score values for determining Top10 status
const getValue = (item) => typeof item[1] === 'object' ? item[1].score || item[1].note : item[1];
let skill1Top10 = getValue(orderSkills[0]) > 90;
let skill2Top10 = getValue(orderSkills[1]) > 90;
let skill3Top10 = getValue(orderSkills[2]) > 90;

let worseSkill = orderSkills[orderSkills.length - 1][0];

// Get all the badges from the results that are obtained
const badgeList = Object.entries(results.value)
  .filter(([key, value]) => key.endsWith("_badge") && value === 1) // Only badges with true
  .map(([key]) => key.replace("_badge", "")); // Remove '_badge' from key

function scrollToTop() {
  window.scrollTo(0, 0);
}

const hasTendances = computed(() => {
  return (
    results.value.leadership_style ||
    results.value.learn_strategy_style ||
    results.value.motivation_style
  );
});
</script>

<style>
.results_v2_back {
  margin: 44px;
}

.result2-got {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 16px;
  position: relative;
  min-width: 0;
}
.result2-got-section {
  align-self: stretch;
  flex-shrink: 0;
  position: relative;
  text-align: center;
  z-index: 2;
}
.result2-got-logo {
  position: relative;
  max-height: 76px;
  min-height: 40px;
}

.result2-got-title {
  position: relative;
  color: #ffffff;
  font-family: "Bio Sans-Regular", Helvetica;
  font-size: 30px;
  font-weight: 600;
  line-height: 36px;
  text-align: center;
  margin-bottom: 16px;
}
.result2-got-text {
  position: relative;
  color: rgba(255, 255, 255, 0.75);
  font-family: "Bio Sans-Regular", Helvetica;
  font-size: 16px;
  font-weight: 400;
  line-height: 0 !important;
  text-align: center;
}

.result2-results-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 24px;
  position: relative;
  min-width: 0;
  margin-top: 48px;
  z-index: 3;
}
.result-v2-area-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 12px;
  position: relative;
  z-index: 4;
}
.result-v2-bullet {
  flex-shrink: 0;
  position: relative;
  width: 38px;
  height: 38px;
  font-size: 0px;
  z-index: 5;
}
.result-v2-bullet-number {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  width: 12px;
  height: 25px;
  margin: 5.867px 0 0 13px;
  color: #000000;
  font-family: "Bio Sans-Regular", Helvetica;
  font-size: 20px;
  font-weight: 700;
  line-height: 24.6px;
  text-align: center;
  white-space: nowrap;
  z-index: 7;
}
.result-v2-bullet-point {
  position: absolute;
  width: 38px;
  height: 38px;
  top: 0;
  left: 0;
  background: url("/images/resultsV2/Ellipse.svg") no-repeat center;
  background-size: cover;
  z-index: 6;
  border-radius: 50%;
}
.result-v2-bullet-title {
  flex-shrink: 0;
  flex-basis: auto;
  position: relative;
  height: 32px;
  color: #ffffff;
  font-family: "Bio Sans-Regular", Helvetica;
  font-size: 26px;
  font-weight: 600;
  line-height: 31.98px;
  text-align: left;
  white-space: nowrap;
  z-index: 8;
}

.result2-top-skills {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 24px;
  position: relative;
  z-index: 10;
}

.result2-subtitle {
  position: relative;
  color: #ffffff;
  font-family: "Bio Sans-Regular", Helvetica;
  font-size: 20px;
  font-weight: 600;
  line-height: 27.06px;
  text-align: left;
}
.result2-subnote {
  position: relative;
  color: rgba(255, 255, 255, 0.75);
  font-family: "Bio Sans-Regular", Helvetica;
  font-size: 16px;
  font-weight: 400;
  line-height: 27.06px;
  text-align: left;
  margin-top: -20px;
}

.result2-badge-container {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(400px, 1fr)
  ); /* Two per row if space allows */
  gap: 16px; /* Space between badges */
  justify-content: start; /* Align items to start of row */
}

.result2-gotop-button {
  margin-top: 24px;
}
</style>
